import { useMemo, useState } from 'react'

import { useQuery } from '@apollo/client'
import { Rule } from 'antd/lib/form'

import { SubcontractorSelectCaptureBlock as SubcontractorSelectCaptureBlockType } from '../../types/blueprint'
import {
  GenericResource,
  GenericResourceSelector,
} from '../GenericResourceSelector'

import { CaptureBlockFormItem } from './CaptureBlockFormItem'

import { Avatar } from '@/components/shared-components/Avatar'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { SubcontractorDisplay } from '@/components/shared-components/SubcontractorDisplay'
import { useLocale } from '@/context/localeContext'
import { useTemplateRenderer } from '@/context/templateRendererContext'
import { PUBLIC_SUBCONTRACTORS } from '@/modules/subcontractors/graphQL'
import { prettyAbn } from '@/utils/prettyCompany'

import { buildPassTheme } from 'src/styles/theme'

import {
  PublicSubcontractorsUiQuery,
  PublicSubcontractorsUiQueryVariables,
} from 'types/api.graphql'

type Props = {
  block: SubcontractorSelectCaptureBlockType
  rules: Rule[]
}

export const SubcontractorSelectCaptureBlock: React.FC<Props> = ({
  block,
  rules,
}) => {
  const { t } = useLocale()
  const { projectId, builderId } = useTemplateRenderer()

  const searchStringState = useState<string>(undefined)

  const { data, loading } = useQuery<
    PublicSubcontractorsUiQuery,
    PublicSubcontractorsUiQueryVariables
  >(PUBLIC_SUBCONTRACTORS, {
    skip: !builderId,
    variables: {
      builderId,
      filter: {
        projectId,
        searchString: searchStringState[0],
        includeBuilderSubcontractor: true,
      },
      pagination: { limit: 5 },
    },
  })

  const items = useMemo<GenericResource[]>(() => {
    const subbies: GenericResource[] = []

    if (data?.subcontractors_public?.length) {
      for (const { id, isBuilder, company } of data.subcontractors_public) {
        const label = isBuilder ? `${company.name} (Builder)` : company.name
        subbies.push({ id: id, label, subLabel: company.abn })
      }
    }

    return subbies
  }, [data?.subcontractors_public])

  if (!builderId) {
    return (
      <CaptureBlockFormItem block={block} rules={[]} initialValue={null}>
        <Text semantic="errorDark">Unable to display Company select</Text>
      </CaptureBlockFormItem>
    )
  }

  return (
    <CaptureBlockFormItem
      block={block}
      initialValue={
        block?.value?.length
          ? block.value.map<GenericResource>((s) => ({
              id: s.id,
              label: s.name,
            }))
          : undefined
      }
      rules={rules}
    >
      <GenericResourceSelector
        searchStringState={searchStringState}
        multiple={block.multiSelect}
        loading={loading}
        items={items}
        headerLabel={t('subcontractor:plural')}
        emptyLabel={`Select ${t(block.multiSelect ? 'subcontractor:plural' : 'subcontractor:singular').toLocaleLowerCase()}`}
        renderItem={(item) => (
          <Flexbox alignItems="center" gap="0.5rem">
            <Avatar
              name={item.label}
              forceColour={buildPassTheme.colour.secondary._300}
            />
            <Flexbox flexDirection="column">
              <Text strong>{item.label}</Text>
              <Text variant="caption01">{prettyAbn(item.subLabel)}</Text>
            </Flexbox>
          </Flexbox>
        )}
        renderResults={(items) => (
          <SubcontractorDisplay
            subcontractors={items.map((s) => ({ company: { name: s.label } }))}
          />
        )}
      />
    </CaptureBlockFormItem>
  )
}
